import { useTranslations } from 'next-intl';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { sendEmailReservation } from '@/app/[locale]/(default-layout)/(forms)/_components/send-email';

import Button from '@/components/button/Button';

import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';
import { IconSuccessful16 } from '@cineamo/ui/src/icons/ic-successful';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { track } from '@/utils/server-side-tracking';

import FormInput from './FormInput';
import FormSelect from './FormSelect';
import { addReservedSeats, getReservedSeats, MAX_RESERVED_SEATS_PER_EMAIL } from './reservation-quota';
import TurnstileWidget from './TurnstileWidget';

/** A single future showing of a content, prepared server-side for the dropdowns. */
export type ReservationShowingOption = {
    id: number;
    label: string;
};

/** A content that has future showings at the cinema. */
export type ReservationContentOption = {
    contentId: number;
    title: string;
    showings: ReservationShowingOption[];
};

/** Payload sent with the reservation email (resolved labels included for the email body). */
export type ReservationFormData = {
    contentId: string;
    contentTitle: string;
    showingId: string;
    showingLabel: string;
    seats: number;
    firstName: string;
    lastName: string;
    email: string;
};

type ReservationFormFields = {
    contentId: string;
    showingId: string;
    seats: number;
    firstName: string;
    lastName: string;
    email: string;
};

type ReservationFormProps = {
    contentOptions: ReservationContentOption[];
    /** Preselected content/showing (e.g. from a `?showingId=` URL param). */
    initialContentId?: string;
    initialShowingId?: string;
};

export default function ReservationForm({ contentOptions, initialContentId, initialShowingId }: ReservationFormProps) {
    const t = useTranslations();
    const domainConfiguration = useDomainConfiguration();
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

    const { register, handleSubmit, watch, reset } = useForm<ReservationFormFields>({
        defaultValues: {
            contentId: initialContentId ?? '',
            showingId: initialShowingId ?? '',
            seats: undefined,
            firstName: '',
            lastName: '',
            email: ''
        }
    });

    const selectedContentId = watch('contentId');

    const selectedContent = useMemo(
        () => contentOptions.find((content) => content.contentId.toString() === selectedContentId),
        [contentOptions, selectedContentId]
    );

    const contentSelectOptions = useMemo(
        () => contentOptions.map((content) => ({ value: content.contentId.toString(), label: content.title })),
        [contentOptions]
    );

    const showingSelectOptions = useMemo(
        () =>
            (selectedContent?.showings ?? []).map((showing) => ({
                value: showing.id.toString(),
                label: showing.label
            })),
        [selectedContent]
    );

    const onSubmit = async (fields: ReservationFormFields) => {
        // Best-effort, client-side guard: a single email may reserve at most
        // MAX_RESERVED_SEATS_PER_EMAIL seats per showing (tracked in localStorage).
        const alreadyReserved = getReservedSeats(fields.email, fields.showingId);
        if (alreadyReserved + fields.seats > MAX_RESERVED_SEATS_PER_EMAIL) {
            showToast({
                toastType: toast.error,
                messageTitle: t('reservation.limit.title'),
                messageBody: t('reservation.limit.message', {
                    max: MAX_RESERVED_SEATS_PER_EMAIL,
                    reserved: alreadyReserved
                })
            });
            return;
        }

        setIsProcessing(true);

        const contentTitle = selectedContent?.title ?? '';
        const showingLabel =
            selectedContent?.showings.find((showing) => showing.id.toString() === fields.showingId)?.label ?? '';

        const data: ReservationFormData = {
            ...fields,
            contentTitle,
            showingLabel
        };

        try {
            const success = await sendEmailReservation(data, turnstileToken ?? undefined);

            if (success) {
                addReservedSeats(data.email, data.showingId, data.seats);
                setIsSuccess(true);
                reset();
                showToast({
                    toastType: toast.success,
                    messageIcon: <IconSuccessful16 />,
                    messageTitle: t('reservation.success.title'),
                    messageBody: t('reservation.success.message'),
                    backgroundColor: domainConfiguration.style.primaryColor,
                    textColor: domainConfiguration.style.onPrimaryColor
                });
            } else {
                showToast({
                    toastType: toast.error,
                    messageTitle: t('reservation.error.title'),
                    messageBody: t('reservation.error.message')
                });
            }

            track('Reservation Form Submitted', {
                cinema: domainConfiguration.cinema?.name,
                domain: domainConfiguration.domain,
                cinemaId: domainConfiguration.cinema?.id,
                data: JSON.stringify(data, null, 2),
                success: success
            });
        } catch {
            showToast({
                toastType: toast.error,
                messageTitle: t('reservation.error.title'),
                messageBody: t('reservation.error.network-message')
            });
        } finally {
            setIsProcessing(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="backdrop-blur-sm rounded-12 p-12 shadow-lg text-center bg-[var(--surface-color)]">
                <IconSuccessful16 className="size-[48px] mx-auto mb-4 text-[var(--primary-color)]" />
                <h3 className="text-headline-24 mb-2 text-[var(--on-background-color)]">
                    {t('reservation.success.title')}
                </h3>
                <p className="text-body-16 text-[rgba(var(--on-background-color-rgb),0.8)]">
                    {t('reservation.success.message')}
                </p>
            </div>
        );
    }

    return (
        <div className="backdrop-blur-sm rounded-12 p-12 shadow-lg bg-[var(--surface-color)]">
            {contentOptions.length === 0 ? (
                <p className="text-body-16 text-center text-[rgba(var(--on-surface-color-rgb),0.8)]">
                    {t('reservation.form.no-showings')}
                </p>
            ) : (
                <form
                    className="space-y-24"
                    onSubmit={handleSubmit(onSubmit)}>
                    {/* Content selection */}
                    <FormSelect
                        name="contentId"
                        label={t('reservation.form.content')}
                        register={register}
                        registerOptions={{ required: true }}
                        required
                        options={contentSelectOptions}
                        placeholder={t('reservation.form.content-placeholder')}
                    />

                    {/* Showing selection (depends on selected content) */}
                    <FormSelect
                        name="showingId"
                        label={t('reservation.form.showing')}
                        register={register}
                        registerOptions={{ required: true }}
                        required
                        disabled={!selectedContent}
                        options={showingSelectOptions}
                        placeholder={t('reservation.form.showing-placeholder')}
                    />

                    {/* Seats */}
                    <FormInput
                        name="seats"
                        label={t('reservation.form.seats')}
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={10}
                        register={register}
                        registerOptions={{ required: true, valueAsNumber: true, min: 1, max: 10 }}
                        required
                        placeholder={t('reservation.form.seats-placeholder')}
                    />

                    {/* Separator */}
                    <div className="w-full h-1 bg-[rgba(var(--on-surface-color-rgb),0.3)]" />

                    {/* Contact information */}
                    <div className="grid md:grid-cols-2 gap-8">
                        <FormInput
                            name="firstName"
                            label={t('reservation.form.first-name')}
                            type="text"
                            register={register}
                            registerOptions={{ required: true }}
                            required
                        />
                        <FormInput
                            name="lastName"
                            label={t('reservation.form.last-name')}
                            type="text"
                            register={register}
                            registerOptions={{ required: true }}
                            required
                        />
                        <FormInput
                            name="email"
                            label={t('reservation.form.email')}
                            type="email"
                            register={register}
                            registerOptions={{ required: true }}
                            required
                        />
                    </div>

                    {/* Required Fields Note */}
                    <div>
                        <p className="text-body-12 text-center text-[rgba(var(--on-surface-color-rgb),0.7)]">
                            {t('reservation.form.required-fields')}
                        </p>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-8">
                        <TurnstileWidget onVerify={setTurnstileToken} />
                        <Button
                            type="submit"
                            disabled={isProcessing}
                            className="w-full py-4 text-body-16 font-semibold transition-all duration-200 rounded-8"
                            css={{
                                backgroundColor: domainConfiguration.style.primaryColor,
                                color: domainConfiguration.style.onPrimaryColor,
                                opacity: isProcessing ? 0.6 : 1
                            }}
                            leadingIcon={isProcessing ? <IconLoading16 className="animate-spin" /> : undefined}>
                            {isProcessing ? t('reservation.form.submitting') : t('reservation.form.submit')}
                        </Button>
                    </div>
                </form>
            )}
        </div>
    );
}
